import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, authService } from "../firebase";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Google Icon Component
const GoogleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" style={{ height: "20px", width: "20px" }} viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-2.641-.21-5.236-.611-7.743z" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C42.022 35.026 44 30.038 44 24c0-2.641-.21-5.236-.611-7.743z" />
  </svg>
);

// Person Add Icon Component
const PersonAddIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" style={{ height: "20px", width: "20px" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="19" y1="8" x2="19" y2="14" />
    <line x1="22" y1="11" x2="16" y2="11" />
  </svg>
);

// Slide images for the carousel
const slideImages = [
  {
    src: process.env.PUBLIC_URL + "/images/Bento-Image-01_1-p-1600.png",
    title: "AI-Powered Study Tools",
    description: "Get instant help with any subject"
  },
  {
    src: process.env.PUBLIC_URL + "/images/Bento-Image-07_1-p-1600.png",
    title: "Smart Flashcards",
    description: "Learn faster with personalized practice"
  },
  {
    src: process.env.PUBLIC_URL + "/images/Bento-Image-08_1-p-1600.png",
    title: "Collaborate with Friends",
    description: "Study together, succeed together"
  }
];

function Login() {
  const [user, authLoading] = useAuthState(auth);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Image slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Redirect if user is logged in
  useEffect(() => {
    if (user) {
      navigate("/login-success");
    }
  }, [user, navigate]);

  // Auto-advance slides for image carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  const nextSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev + 1) % slideImages.length);
  };

  const prevSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev - 1 + slideImages.length) % slideImages.length);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError("");
    try {
      await authService.signInWithGoogle();
      // After successful login, user will be redirected by the useEffect
    } catch (error) {
      console.error("Error signing in with Google:", error);
      setError("Failed to sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#0a0a0a" }}>
        <p style={{ color: "white", fontSize: "18px" }}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", backgroundColor: "#0a0a0a" }}>
      {/* Left Column - Authentication Form */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "48px" }}>
        <div style={{ width: "100%", maxWidth: "400px" }}>
          {/* Title and Description - Centered */}
          <h1 style={{ fontSize: "1.75rem", fontWeight: 600, color: "#ffffff", marginBottom: "8px", textAlign: "center" }}>
            Get Started with EduTools
          </h1>
          <p style={{ color: "rgba(255,255,255,0.5)", marginBottom: "24px", fontSize: "14px", textAlign: "center" }}>
            Sign in or create an account to continue.
          </p>

          {/* Error Message */}
          {error && (
            <div style={{ padding: "12px 16px", marginBottom: "16px", backgroundColor: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "8px", color: "#fca5a5", fontSize: "14px" }}>
              {error}
            </div>
          )}

          {/* Sign in with Google Button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "12px 16px",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "6px",
              backgroundColor: "transparent",
              color: "#ffffff",
              fontWeight: 400,
              fontSize: "14px",
              cursor: loading ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              opacity: loading ? 0.7 : 1,
              marginBottom: "12px",
            }}
            onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = "rgba(255,255,255,0.05)")}
            onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = "transparent")}
          >
            <GoogleIcon />
            {loading ? "Signing in..." : "Sign in with Google"}
          </button>

          {/* Create an account Button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "12px 16px",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "6px",
              backgroundColor: "transparent",
              color: "#ffffff",
              fontWeight: 400,
              fontSize: "14px",
              cursor: loading ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              opacity: loading ? 0.7 : 1,
            }}
            onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = "rgba(255,255,255,0.05)")}
            onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = "transparent")}
          >
            <PersonAddIcon />
            {loading ? "Loading..." : "Create an account"}
          </button>

          {/* Terms */}
          <p style={{ fontSize: "11px", color: "rgba(255,255,255,0.4)", marginTop: "20px", textAlign: "center" }}>
            By continuing, you agree to our{" "}
            <Link to="/terms-of-service" style={{ color: "rgba(255,255,255,0.6)", textDecoration: "underline" }}>
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy-policy" style={{ color: "rgba(255,255,255,0.6)", textDecoration: "underline" }}>
              Privacy Policy
            </Link>
            .
          </p>

          {/* DEV BYPASS - Remove before production */}
          {process.env.NODE_ENV === "development" && (
            <button
              onClick={() => navigate("/login-success")}
              style={{
                width: "100%",
                marginTop: "16px",
                padding: "10px 16px",
                border: "1px dashed rgba(251, 191, 36, 0.4)",
                borderRadius: "6px",
                backgroundColor: "rgba(251, 191, 36, 0.08)",
                color: "rgba(251, 191, 36, 0.7)",
                fontSize: "12px",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(251, 191, 36, 0.15)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(251, 191, 36, 0.08)")}
            >
              [DEV] Continue → New User Onboarding
            </button>
          )}
        </div>
      </div>

      {/* Right Column - Image Slider */}
      <div style={{ flex: 1, position: "relative", margin: "16px", borderRadius: "24px", overflow: "hidden", background: "linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%)" }}>
        {/* Slide Images */}
        <div style={{ position: "absolute", inset: 0 }}>
          {slideImages.map((slide, index) => (
            <div
              key={index}
              style={{
                position: "absolute",
                inset: 0,
                transition: "opacity 0.7s ease-in-out",
                opacity: index === currentSlide ? 1 : 0,
              }}
            >
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 50%)", zIndex: 10 }} />
              <img
                src={slide.src}
                alt={slide.title}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          ))}
        </div>

        {/* Slide Content */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "32px", zIndex: 20 }}>
          <div style={{ maxWidth: "400px" }}>
            <h2 style={{ fontSize: "1.75rem", fontWeight: 600, color: "white", marginBottom: "8px" }}>
              {slideImages[currentSlide].title}
            </h2>
            <p style={{ color: "rgba(255,255,255,0.8)", fontSize: "1rem" }}>
              {slideImages[currentSlide].description}
            </p>
          </div>

          {/* Slide Navigation */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "32px" }}>
            {/* Dots */}
            <div style={{ display: "flex", gap: "8px" }}>
              {slideImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setIsAutoPlaying(false);
                    setCurrentSlide(index);
                  }}
                  style={{
                    height: "8px",
                    borderRadius: "4px",
                    transition: "all 0.3s",
                    width: index === currentSlide ? "32px" : "8px",
                    backgroundColor: index === currentSlide ? "white" : "rgba(255,255,255,0.4)",
                    border: "none",
                    cursor: "pointer",
                  }}
                />
              ))}
            </div>

            {/* Arrow Controls */}
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={prevSlide}
                style={{
                  padding: "8px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  border: "none",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.3)"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.2)"}
              >
                <ChevronLeft style={{ width: "20px", height: "20px", color: "white" }} />
              </button>
              <button
                onClick={nextSlide}
                style={{
                  padding: "8px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  border: "none",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.3)"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.2)"}
              >
                <ChevronRight style={{ width: "20px", height: "20px", color: "white" }} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
